/**
 * The device location a message send carries, kept so no send has to wait for
 * one. Resolving a location is slow, and while the permission prompt is open it
 * does not finish at all, so a send reads only what is already here and warms
 * the cache for the next one (BIG-9405). Shared so web and native keep the
 * same freshness window and the same one-lookup-at-a-time rule.
 */

/** How long a resolved location stays fresh enough to send. */
export const LOCATION_CACHE_MS = 60_000;

export type LocationCache = {
  /** A location resolved within the window, or null. Never waits. */
  getCached: () => string | null;
  /** Resolve now, joining a lookup already in flight. Rejects as `resolve` does. */
  resolveOnce: () => Promise<string | null>;
  /**
   * Start a lookup in the background unless one is fresh or running. Never
   * throws, so it is safe on the send path; a failure leaves the cache empty.
   */
  warm: () => void;
};

/**
 * One cache per resolver. Callers keep it at module scope: composers remount on
 * navigation, and a cache per instance would never warm the first send.
 */
export const createLocationCache = (resolve: () => Promise<string | null>): LocationCache => {
  let cached: { location: string; timestamp: number } | null = null;
  let inFlight: Promise<string | null> | null = null;

  const getCached = (): string | null =>
    cached && Date.now() - cached.timestamp < LOCATION_CACHE_MS ? cached.location : null;

  const resolveOnce = (): Promise<string | null> => {
    if (!inFlight) {
      inFlight = resolve()
        .then((location) => {
          if (location) cached = { location, timestamp: Date.now() };
          return location;
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  const warm = (): void => {
    if (inFlight || getCached()) return;
    resolveOnce().catch(() => {
      // The next send goes without a location, and warms again.
    });
  };

  return { getCached, resolveOnce, warm };
};
